import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { PlayerProvider } from "@/context/PlayerContext";

import Layout from "@/components/Layout";
import AudioPlayer from "@/components/AudioPlayer";

import AuthPage from "@/pages/Auth";
import FeedPage from "@/pages/Feed";
import StorePage from "@/pages/Store";
import LibraryPage from "@/pages/Library";
import ProfilePage from "@/pages/Profile";
import AgendaPage from "@/pages/Agenda";
import LivesPage from "@/pages/Live";
import DashboardPage from "@/pages/Dashboard";
import ChallengesPage from "@/pages/Challenges";
import InstallPage from "@/pages/Install";
import DiscoverPage from "@/pages/Discover";
import AdminApp from "@/pages/Admin";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/Payment";
import PrivacyPage from "@/pages/legal/Privacy";
import TermsPage from "@/pages/legal/Terms";
import GeneralPage from "@/pages/legal/General";
import DeleteAccountPage from "@/pages/legal/DeleteAccount";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import SupportPage from "@/pages/Support";
import ReleasePage from "@/pages/Release";
import AffiliatePage from "@/pages/Affiliate";
import FaqPage from "@/pages/legal/Faq";
import TermsGate from "@/components/legal/TermsGate";
import CookieBanner from "@/components/legal/CookieBanner";

import AntiPiracyOverlay from "@/components/AntiPiracyOverlay";
import useAntiPiracy from "@/lib/useAntiPiracy";
import { usePlayer } from "@/context/PlayerContext";

function Protected({ children }) {
  const { user, booted } = useAuth();
  if (!booted) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="w-12 h-12 rounded-full border-2 border-[#FF0050] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  // Admins can browse the platform like anyone else (feed, store, library...)
  // — only the initial post-login redirect sends them to /admin first (see
  // Auth.jsx's submit handler); this used to force them back to /admin on
  // every single page, making the rest of the app unreachable while logged
  // in as admin.
  return children;
}

function AppShell() {
  const { recording, dismiss } = useAntiPiracy({ active: true });
  const player = usePlayer();
  useEffect(() => {
    if (recording && player?.pause) player.pause();
  }, [recording, player]);

  return (
    <>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/install" element={<InstallPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/policy" element={<GeneralPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/admin/*" element={<AdminApp />} />
        <Route
          path="*"
          element={
            <Protected>
              <Layout>
                <Routes>
                  <Route path="/" element={<FeedPage />} />
                  <Route path="/store" element={<StorePage />} />
                  <Route path="/library" element={<LibraryPage />} />
                  <Route path="/discover" element={<DiscoverPage />} />
                  <Route path="/profile/:id" element={<ProfilePage />} />
                  <Route path="/release/:id" element={<ReleasePage />} />
                  <Route path="/agenda" element={<AgendaPage />} />
                  <Route path="/lives" element={<LivesPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/challenges" element={<ChallengesPage />} />
                  <Route path="/affiliation" element={<AffiliatePage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
              <AudioPlayer />
              <TermsGate />
            </Protected>
          }
        />
      </Routes>
      <AntiPiracyOverlay visible={recording} onDismiss={dismiss} />
      <CookieBanner />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const FALLBACK = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='%23FF0050'/><stop offset='100%' stop-color='%238A2BE2'/></linearGradient></defs><rect width='400' height='400' fill='url(%23g)' opacity='0.25'/><text x='50%' y='50%' font-family='Arial' font-size='20' fill='white' opacity='0.5' text-anchor='middle' dominant-baseline='middle'>Metazik</text></svg>";
    const onError = (e) => {
      const t = e.target;
      if (t && t.tagName === "IMG" && !t.dataset.fallbackApplied) {
        t.dataset.fallbackApplied = "1";
        t.src = FALLBACK;
      }
    };
    document.addEventListener("error", onError, true);
    return () => document.removeEventListener("error", onError, true);
  }, []);

  return (
    <I18nProvider>
      <AuthProvider>
        <PlayerProvider>
          <BrowserRouter>
            <AppShell />
            {/* short messages appear at the top, just under the header: never over the
                player, its buttons or the bottom menu */}
            <Toaster
              position="top-center"
              offset={76}
              mobileOffset={76}
              theme="dark"
              toastOptions={{
                style: { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" },
              }}
            />
          </BrowserRouter>
        </PlayerProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
